import { useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import Header from "../../Header";
import Sidebar from "../../Sidebar";

import {
  FaBoxArchive,
  FaBuilding,
  FaLayerGroup,
  FaCreditCard,
  FaBuildingUser,
  FaCity,
  FaFolderOpen,
  FaBullseye,
  FaUsersGear,
  FaGear,
  FaReceipt,
  FaFileInvoiceDollar,
  FaMoneyBillTrendUp,
  FaTableColumns,
  FaClockRotateLeft,
  FaBell,
  FaListCheck,
  FaCircleUser,
  FaGaugeHigh,
} from "react-icons/fa6";
import { RiHome3Fill } from "react-icons/ri";
import useApi from "@/hooks/useApi";
import { useSettingsStore, useCompanyStore, useLoggedUserStore } from "@/Store";

import S from "./styles.module.scss";
import { canAccessModuleRoute, moduleForPath } from "@/Router/moduleAccess";
import { ISidebar } from "@/types/ISidebar";

enum Permission {
  Admin = "Admin",
  Comum = "Comum",
}

const baseSidebar: ISidebar["sidebarData"] = [
  // --- Contexto GERAL (não dependem da empresa selecionada) ---
  {
    label: "Visão geral",
    path: "/visao-geral",
    icon: FaGaugeHigh,
    active: false,
    permission: [Permission.Admin, Permission.Comum],
    context: "geral",
  },
  {
    label: "Minha conta",
    path: "/perfil",
    icon: FaCircleUser,
    active: false,
    permission: [Permission.Admin, Permission.Comum],
    context: "geral",
  },
  {
    label: "Notificações",
    path: "/notificacoes",
    icon: FaBell,
    active: false,
    permission: [Permission.Admin, Permission.Comum],
    context: "geral",
  },
  {
    label: "Usuários",
    path: "/usuarios",
    icon: FaUsersGear,
    active: false,
    permission: [Permission.Admin],
    module: "users",
    context: "geral",
  },
  {
    label: "Plano",
    path: "/plano",
    icon: FaCreditCard,
    active: false,
    permission: [Permission.Admin],
    ownerOnly: true,
    context: "geral",
  },
  {
    label: "Empresas",
    path: "/empresas",
    icon: FaBuilding,
    active: false,
    permission: [],
    accountOwnerOrStaff: true,
    context: "geral",
  },
  {
    label: "Contas",
    path: "/contas",
    icon: FaLayerGroup,
    active: false,
    permission: [],
    superAdmin: true,
    context: "geral",
  },
  // --- Contexto EMPRESA (dependem da empresa selecionada no header) ---
  {
    label: "Início",
    path: "/inicio",
    icon: RiHome3Fill,
    active: false,
    permission: [Permission.Admin, Permission.Comum],
    context: "empresa",
  },
  {
    label: "Meu dia",
    path: "/meu-dia",
    icon: FaListCheck,
    active: false,
    permission: [Permission.Admin, Permission.Comum],
    context: "empresa",
  },
  {
    label: "Departamentos",
    path: "/departamentos",
    icon: FaBuildingUser,
    active: false,
    permission: [Permission.Admin],
    module: "departments",
    context: "empresa",
  },
  {
    label: "Leads",
    path: "/leads",
    icon: FaBullseye,
    active: false,
    permission: [Permission.Admin],
    module: "leads",
    context: "empresa",
  },
  {
    label: "Clientes",
    path: "/clientes",
    icon: FaCity,
    active: false,
    permission: [Permission.Admin],
    module: "clients",
    context: "empresa",
  },
  {
    label: "Contatos",
    path: "/contatos",
    icon: FaUsersGear,
    active: false,
    permission: [Permission.Admin],
    module: "contacts",
    context: "empresa",
  },
  {
    label: "Projetos",
    path: "/projetos",
    icon: FaBoxArchive,
    active: false,
    permission: [Permission.Admin],
    module: "projects",
    context: "empresa",
  },
  {
    label: "Assinantes",
    path: "/assinantes",
    icon: FaReceipt,
    active: false,
    permission: [Permission.Admin],
    module: "subscriptions",
    context: "empresa",
  },
  {
    label: "Financeiro",
    path: "/financeiro",
    icon: FaMoneyBillTrendUp,
    active: false,
    permission: [Permission.Admin],
    module: "finance",
    context: "empresa",
  },
  {
    label: "Faturas",
    path: "/faturas",
    icon: FaFileInvoiceDollar,
    active: false,
    permission: [Permission.Admin],
    module: "finance",
    context: "empresa",
  },
  {
    label: "Documentos",
    path: "/documentos",
    icon: FaFolderOpen,
    active: false,
    permission: [Permission.Admin, Permission.Comum],
    module: "documents",
    context: "empresa",
  },
  {
    label: "Kanban",
    path: "/kanban",
    icon: FaTableColumns,
    active: false,
    permission: [Permission.Admin, Permission.Comum],
    module: "kanban",
    context: "empresa",
  },
  {
    label: "Configurações",
    path: "/configuracoes",
    icon: FaGear,
    active: false,
    permission: [Permission.Admin],
    context: "empresa",
  },
  {
    label: "Auditoria",
    path: "/auditoria",
    icon: FaClockRotateLeft,
    active: false,
    permission: [Permission.Admin],
    context: "empresa",
  },
];

const Layout = () => {
  const { getRequest } = useApi();
  const location = useLocation();
  const settings = useSettingsStore((state) => state.settings);
  const setSettings = useSettingsStore((state) => state.setSettings);
  const currentCompanyId = useCompanyStore((state) => state.currentCompanyId);
  const loggedUser = useLoggedUserStore((state) => state.loggedUser);
  const setLoggedUser = useLoggedUserStore((state) => state.setLoggedUser);
  const [loadedCompanyId, setLoadedCompanyId] = useState<
    number | null | undefined
  >(undefined);
  const [accessLoadFailed, setAccessLoadFailed] = useState(false);
  const [accessAttempt, setAccessAttempt] = useState(0);

  useEffect(() => {
    let active = true;

    if (currentCompanyId === null) {
      setLoadedCompanyId(null);
      setAccessLoadFailed(false);
      return () => {
        active = false;
      };
    }

    setAccessLoadFailed(false);

    const loadCompanyAccess = async () => {
      try {
        const [settingsResponse, userResponse] = await Promise.all([
          getRequest("/api/settings"),
          getRequest("/api/user"),
        ]);
        const [settingsData, user] = await Promise.all([
          settingsResponse.json(),
          userResponse.json(),
        ]);
        if (!active) return;
        if (settingsData?.response) setSettings(settingsData.response);
        setLoggedUser(user);
        setLoadedCompanyId(currentCompanyId);
      } catch {
        if (active) setAccessLoadFailed(true);
      }
    };
    loadCompanyAccess();
    return () => {
      active = false;
    };
  }, [currentCompanyId, accessAttempt]);

  const sidebar = useMemo(() => {
    const modules = settings?.modules;
    if (!modules) return baseSidebar;
    return baseSidebar.filter(
      (item) => !item.module || modules[item.module] !== false
    );
  }, [settings]);

  const currentModule = moduleForPath(location.pathname);
  const accessReady = loadedCompanyId === currentCompanyId;
  const blocked =
    accessReady &&
    !canAccessModuleRoute({
      module: currentModule,
      userType: loggedUser?.type,
      userModules: loggedUser?.modules ?? [],
      companyModules: settings?.modules,
    });

  return (
    <div className={S["grid-container"]}>
      <Header sidebarData={sidebar} />
      <Sidebar sidebarData={sidebar} />
      {!accessReady ? (
        <main className={S.accessState} aria-live="polite">
          <p role="status">
            {accessLoadFailed
              ? "Não foi possível carregar os acessos da empresa."
              : "Carregando acessos da empresa…"}
          </p>
          {accessLoadFailed && (
            <button
              type="button"
              onClick={() => setAccessAttempt((n) => n + 1)}
            >
              Tentar novamente
            </button>
          )}
        </main>
      ) : blocked ? (
        <Navigate to="/visao-geral" replace />
      ) : (
        <Outlet key={currentCompanyId ?? "no-company"} />
      )}
    </div>
  );
};

export default Layout;
